import { useToastContext } from '@payaca/ambrosia';
import { useMemo } from 'react';

import { useUpdateMyPreferences } from '@/api/mutations/user/useUpdateMyPreferences';
import {
  type SenderChoice,
  type SenderOption,
  toSenderOption,
} from './senderChoice';

export type SenderChoiceControl = {
  options: SenderOption[];
  canChoose: boolean;
  /** The sender the send will go out as, whether or not a choice is offered. */
  selected?: SenderOption;
  onChoose: (key: string) => void;
};

/**
 * Drives a sender picker from the signed-in user's `emailSenderChoice`, writing
 * each choice through as their remembered preference.
 *
 * Deliberately holds no local selection: the send resolves its sender from the
 * stored preference, so showing the pick before the server has it would promise
 * a sender the send might not use. A choice that fails to save leaves the
 * picker on the sender the send will really go out as.
 */
export const useSenderChoice = (choice?: SenderChoice): SenderChoiceControl => {
  const { pushError } = useToastContext();
  const updateMyPreferences = useUpdateMyPreferences();
  const options = useMemo(
    () => choice?.options.map(toSenderOption) ?? [],
    [choice]
  );

  const onChoose = (key: string) => {
    const option = options.find((candidate) => candidate.key === key);
    if (!option) return;

    updateMyPreferences.mutate(
      { emailSender: option.input },
      {
        onError: () =>
          pushError(
            `Couldn't save ${option.label} as your sender. This email will be sent from the sender shown instead.`
          ),
      }
    );
  };

  return {
    options,
    canChoose: options.length > 1,
    selected: choice && toSenderOption(choice.chosen),
    onChoose,
  };
};
